import React, { useState, useEffect } from "react";
import axios from "axios";
import proxy from "../../proxy";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  useToast,
  SimpleGrid,
  Text,
  Select,
  FormHelperText,
} from "@chakra-ui/react";

const StockModal = ({ isOpen, onClose, stock, onSave }) => {
  const [formData, setFormData] = useState({
    company: "",
    ticker: "",
    nr_shares: "",
    average_price_share: "",
    div_share: "",
    div_per_year: "",
    paid_div: "",
    commission: "",
    div_growth_years: "",
    div_status: "growing",
    div_status_year: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const toast = useToast();

  useEffect(() => {
    if (stock) {
      setFormData({
        company: stock.company || "",
        ticker: stock.ticker || "",
        nr_shares: stock.nr_shares || "",
        average_price_share: stock.average_price_share || "",
        div_share: stock.div_share || "",
        div_per_year: stock.div_per_year || "",
        paid_div: stock.paid_div || "",
        commission: stock.commission || "",
        // 0 e o valoare validă (streak rupt), deci nu se poate folosi `|| ""`
        div_growth_years:
          stock.div_growth_years === null || stock.div_growth_years === undefined
            ? ""
            : String(stock.div_growth_years),
        div_status: stock.div_status || "growing",
        div_status_year:
          stock.div_status_year === null || stock.div_status_year === undefined
            ? ""
            : String(stock.div_status_year),
      });
    } else {
      setFormData({
        company: "",
        ticker: "",
        nr_shares: "",
        average_price_share: "",
        div_share: "",
        div_per_year: "",
        paid_div: "",
        commission: "",
        div_growth_years: "",
        div_status: "growing",
        div_status_year: "",
      });
    }
  }, [stock, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
      // "growing" nu are an asociat — golim campul ca sa nu ramana o valoare veche
      ...(name === "div_status" && value === "growing" ? { div_status_year: "" } : {}),
    }));
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.company || !formData.ticker || !formData.nr_shares) {
      toast({
        title: "Missing fields",
        description: "Company, Ticker, and Number of Shares are required",
        status: "warning",
        duration: 3000,
        position: "top",
      });
      return;
    }

    setIsLoading(true);

    try {
      if (stock) {
        // Update
        await axios.post(`${proxy}server_mts/admin/UpdateStock.php`, {
          id: stock.id,
          ...formData,
        });
        toast({
          title: "Stock updated successfully",
          status: "success",
          duration: 2000,
          position: "top",
        });
      } else {
        // Add
        await axios.post(`${proxy}server_mts/admin/AddStock.php`, formData);
        toast({
          title: "Stock added successfully",
          status: "success",
          duration: 2000,
          position: "top",
        });
      }

      onSave();
      setTimeout(() => {
        onClose();
      }, 100);
    } catch (error) {
      toast({
        title: "Error saving stock",
        description: error.message,
        status: "error",
        duration: 3000,
        position: "top",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(10px)" />
      <ModalContent bg="gray.800" borderColor="whiteAlpha.200" border="1px solid">
        <ModalHeader color="white">
          {stock ? "Edit Stock" : "Add New Stock"}
        </ModalHeader>
        <ModalCloseButton color="gray.400" />
        <ModalBody>
          <VStack spacing={4}>
            {/* Company & Ticker */}
            <SimpleGrid columns={2} spacing={4} w="full">
              <FormControl isRequired>
                <FormLabel color="gray.300" fontSize="sm">
                  Company Name
                </FormLabel>
                <Input
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  placeholder="e.g., Realty Income Corporation"
                  bg="whiteAlpha.100"
                  border="1px solid"
                  borderColor="whiteAlpha.300"
                  color="white"
                  _hover={{ borderColor: "whiteAlpha.400" }}
                  _focus={{ borderColor: "teal.400" }}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel color="gray.300" fontSize="sm">
                  Ticker Symbol
                </FormLabel>
                <Input
                  name="ticker"
                  value={formData.ticker}
                  onChange={handleChange}
                  placeholder="e.g., O"
                  bg="whiteAlpha.100"
                  border="1px solid"
                  borderColor="whiteAlpha.300"
                  color="white"
                  textTransform="uppercase"
                  _hover={{ borderColor: "whiteAlpha.400" }}
                  _focus={{ borderColor: "teal.400" }}
                />
              </FormControl>
            </SimpleGrid>

            {/* Shares & Avg Price */}
            <SimpleGrid columns={2} spacing={4} w="full">
              <FormControl isRequired>
                <FormLabel color="gray.300" fontSize="sm">
                  Number of Shares
                </FormLabel>
                <Input
                  name="nr_shares"
                  type="number"
                  step="0.01"
                  value={formData.nr_shares}
                  onChange={handleChange}
                  placeholder="4.07"
                  bg="whiteAlpha.100"
                  border="1px solid"
                  borderColor="whiteAlpha.300"
                  color="white"
                  _hover={{ borderColor: "whiteAlpha.400" }}
                  _focus={{ borderColor: "teal.400" }}
                />
              </FormControl>

              <FormControl>
                <FormLabel color="gray.300" fontSize="sm">
                  Average Price/Share ($)
                </FormLabel>
                <Input
                  name="average_price_share"
                  type="number"
                  step="0.01"
                  value={formData.average_price_share}
                  onChange={handleChange}
                  placeholder="57.53"
                  bg="whiteAlpha.100"
                  border="1px solid"
                  borderColor="whiteAlpha.300"
                  color="white"
                  _hover={{ borderColor: "whiteAlpha.400" }}
                  _focus={{ borderColor: "teal.400" }}
                />
              </FormControl>
            </SimpleGrid>

            {/* Dividend Info */}
            <Text color="teal.300" fontSize="sm" fontWeight="bold" w="full" mt={2}>
              💰 Dividend Information
            </Text>

            <SimpleGrid columns={2} spacing={4} w="full">
              <FormControl>
                <FormLabel color="gray.300" fontSize="sm">
                  Dividend/Share ($)
                </FormLabel>
                <Input
                  name="div_share"
                  type="number"
                  step="0.0001"
                  value={formData.div_share}
                  onChange={handleChange}
                  placeholder="0.2681"
                  bg="whiteAlpha.100"
                  border="1px solid"
                  borderColor="whiteAlpha.300"
                  color="white"
                  _hover={{ borderColor: "whiteAlpha.400" }}
                  _focus={{ borderColor: "teal.400" }}
                />
              </FormControl>

              <FormControl>
                <FormLabel color="gray.300" fontSize="sm">
                  Dividends/Year
                </FormLabel>
                <Input
                  name="div_per_year"
                  type="number"
                  value={formData.div_per_year}
                  onChange={handleChange}
                  placeholder="12"
                  bg="whiteAlpha.100"
                  border="1px solid"
                  borderColor="whiteAlpha.300"
                  color="white"
                  _hover={{ borderColor: "whiteAlpha.400" }}
                  _focus={{ borderColor: "teal.400" }}
                />
              </FormControl>
            </SimpleGrid>

            {/* Paid Dividends & Commission */}
            <SimpleGrid columns={2} spacing={4} w="full">
              <FormControl>
                <FormLabel color="gray.300" fontSize="sm">
                  Total Paid Dividends ($)
                </FormLabel>
                <Input
                  name="paid_div"
                  type="number"
                  step="0.01"
                  value={formData.paid_div}
                  onChange={handleChange}
                  placeholder="4.39"
                  bg="whiteAlpha.100"
                  border="1px solid"
                  borderColor="whiteAlpha.300"
                  color="white"
                  _hover={{ borderColor: "whiteAlpha.400" }}
                  _focus={{ borderColor: "teal.400" }}
                />
              </FormControl>

              <FormControl>
                <FormLabel color="gray.300" fontSize="sm">
                  Commission ($)
                </FormLabel>
                <Input
                  name="commission"
                  type="number"
                  step="0.01"
                  value={formData.commission}
                  onChange={handleChange}
                  placeholder="3.3"
                  bg="whiteAlpha.100"
                  border="1px solid"
                  borderColor="whiteAlpha.300"
                  color="white"
                  _hover={{ borderColor: "whiteAlpha.400" }}
                  _focus={{ borderColor: "teal.400" }}
                />
              </FormControl>
            </SimpleGrid>

            {/* Streak-ul de creștere a dividendului */}
            <Text color="teal.300" fontSize="sm" fontWeight="bold" w="full" mt={2}>
              📈 Dividend Growth Streak
            </Text>

            <SimpleGrid columns={3} spacing={4} w="full">
              <FormControl>
                <FormLabel color="gray.300" fontSize="sm">
                  Years of Growth
                </FormLabel>
                <Input
                  name="div_growth_years"
                  type="number"
                  value={formData.div_growth_years}
                  onChange={handleChange}
                  placeholder="33"
                  bg="whiteAlpha.100"
                  border="1px solid"
                  borderColor="whiteAlpha.300"
                  color="white"
                  _hover={{ borderColor: "whiteAlpha.400" }}
                  _focus={{ borderColor: "teal.400" }}
                />
                <FormHelperText color="gray.500" fontSize="xs">
                  Leave empty if unknown
                </FormHelperText>
              </FormControl>

              <FormControl>
                <FormLabel color="gray.300" fontSize="sm">
                  Status
                </FormLabel>
                <Select
                  name="div_status"
                  value={formData.div_status}
                  onChange={handleChange}
                  bg="whiteAlpha.100"
                  border="1px solid"
                  borderColor="whiteAlpha.300"
                  color="white"
                  _hover={{ borderColor: "whiteAlpha.400" }}
                  _focus={{ borderColor: "teal.400" }}
                >
                  <option value="growing" style={{ background: "#2D3748" }}>Growing</option>
                  <option value="frozen" style={{ background: "#2D3748" }}>Frozen</option>
                  <option value="cut" style={{ background: "#2D3748" }}>Cut</option>
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel color="gray.300" fontSize="sm">
                  Freeze / Cut Year
                </FormLabel>
                <Input
                  name="div_status_year"
                  type="number"
                  value={formData.div_status_year}
                  onChange={handleChange}
                  placeholder="2025"
                  isDisabled={formData.div_status === "growing"}
                  bg="whiteAlpha.100"
                  border="1px solid"
                  borderColor="whiteAlpha.300"
                  color="white"
                  _hover={{ borderColor: "whiteAlpha.400" }}
                  _focus={{ borderColor: "teal.400" }}
                />
                <FormHelperText color="gray.500" fontSize="xs">
                  Only for frozen/cut
                </FormHelperText>
              </FormControl>
            </SimpleGrid>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button
            colorScheme="teal"
            onClick={handleSubmit}
            isLoading={isLoading}
          >
            {stock ? "Update" : "Add"} Stock
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default StockModal;